/**
 * CastingSystem — OWNER: core builder.
 * GCD (haste-scaled, clamped), 400 ms spell queue, cast/channel progress with haste,
 * pushback, cooldowns with charges, resource costs/gains, school lockouts, range/facing
 * via spatial, usableIf via combat, cast-time/cost/cooldown-rate aura mods.
 */
import type {
  AbilityDef,
  AbilityId,
  CastFailReason,
  CastState,
  CastingSystem,
  ContentRegistry,
  CooldownState,
  DeepReadonly,
  EffectCtx,
  Ms,
  SimContext,
  StatModKind,
  Unit,
  UnitId,
  Vec2,
  World,
} from '../contracts';
import {
  BASE_GCD_MS,
  CAST_PUSHBACK_MS,
  CHANNEL_PUSHBACK_FRACTION,
  MAX_PUSHBACKS,
  MIN_GCD_MS,
  SPELL_QUEUE_WINDOW_MS,
} from '../contracts';
import { startAttackFromAbility } from '../combat/melee'; // +autoattack
import { isAirborne } from '../contracts'; // +glide
import { abilityLocked } from '../field/heroes'; // +field

/** A queued cast that cannot fire within this long after queueing is dropped. */
export const QUEUE_EXPIRY_MS: Ms = 2000;

type Resolved = { ok: true; targetId: UnitId | null; point: Vec2 | null } | { ok: false; reason: CastFailReason };

const fail = (reason: CastFailReason): { ok: false; reason: CastFailReason } => ({ ok: false, reason });

// ---------------------------------------------------------------------------
// Aura-mod helpers (EffectiveStats does not carry per-ability mods)
// ---------------------------------------------------------------------------

/** Each matching aura mod contributes v × stacks. */
function eachMod(ctx: Pick<SimContext, 'content'>, u: DeepReadonly<Unit>, stat: StatModKind, abilityId: AbilityId, fn: (v: number) => void): void {
  for (const inst of u.auras) {
    const def = ctx.content.aura(inst.auraId);
    if (!def?.mods) continue;
    for (const m of def.mods) {
      if (m.stat !== stat) continue;
      if (m.abilityIds && !m.abilityIds.includes(abilityId)) continue;
      fn(m.value * (m.ignoreStacks ? 1 : inst.stacks)); // +lead (F8)
    }
  }
}

/** Product of (1 + v) over matching mods, floored at 0. */
export function modMultiplier(ctx: SimContext, u: Unit, stat: StatModKind, abilityId: AbilityId): number {
  let mult = 1;
  eachMod(ctx, u, stat, abilityId, (v) => {
    mult *= Math.max(0, 1 + v);
  });
  return mult;
}

/** Sum of v over matching mods. */
export function modSum(ctx: SimContext, u: Unit, stat: StatModKind, abilityId: AbilityId): number {
  let sum = 0;
  eachMod(ctx, u, stat, abilityId, (v) => {
    sum += v;
  });
  return sum;
}

function hasteOf(ctx: SimContext, unitId: UnitId): number {
  return clampHaste(ctx.sys.combat.effectiveStats(ctx, unitId).haste);
}

const clampHaste = (h: number): number => Math.max(-0.9, h);

/** Hasted GCD for an ability (TC TriggerGlobalCooldown: haste and the [750, 1500] clamp apply only to base GCDs inside that range). */
export function gcdFor(ability: Pick<AbilityDef, 'gcdMs'>, haste: number): Ms {
  const base = ability.gcdMs ?? BASE_GCD_MS;
  if (base <= 0) return 0;
  if (base > BASE_GCD_MS || base < MIN_GCD_MS) return base;
  const hasted = base / (1 + clampHaste(haste));
  return Math.round(Math.min(BASE_GCD_MS, Math.max(MIN_GCD_MS, hasted)));
}

/** Hasted, aura-modified cast or channel duration (0 = instant). */
export function castTimeFor(content: Pick<ContentRegistry, 'aura'>, u: DeepReadonly<Unit>, ab: AbilityDef, haste: number): Ms {
  const c = ab.cast;
  if (!c || c.type === 'instant') return 0;
  const base = c.type === 'cast' ? c.timeMs : c.durationMs;
  let mult = 1;
  if (c.type === 'cast') eachMod({ content: content as ContentRegistry }, u, 'castTimePct', ab.id, (v) => (mult *= Math.max(0, 1 + v)));
  return Math.max(0, Math.round((base * mult) / (1 + clampHaste(haste))));
}

/**
 * Cast timing exactly as the sim will apply it (pure, read-only). Used by the client to predict
 * the GCD sweep and cast bar on key press, before the sim confirms the cast.
 */
export function castTiming(content: Pick<ContentRegistry, 'aura'>, u: DeepReadonly<Unit>, ab: AbilityDef, haste: number): { gcdMs: Ms; castMs: Ms; kind: 'instant' | 'cast' | 'channel' } {
  const kind = ab.cast?.type ?? 'instant';
  return { gcdMs: gcdFor(ab, haste), castMs: castTimeFor(content, u, ab, haste), kind };
}

// ---------------------------------------------------------------------------
// Cooldown charges
// ---------------------------------------------------------------------------

/** Bring a cooldown up to date: grant every charge whose recharge time has passed. */
export function syncCooldown(cd: CooldownState, now: Ms): void {
  while (cd.rechargeAt !== null && cd.rechargeAt <= now && cd.charges < cd.maxCharges) {
    cd.charges += 1;
    cd.rechargeAt = cd.charges < cd.maxCharges ? cd.rechargeAt + cd.rechargeDurationMs : null;
  }
  if (cd.charges >= cd.maxCharges) {
    cd.charges = cd.maxCharges;
    cd.rechargeAt = null;
  }
}

/** Refresh every unit's cooldown charges (tick step 2). */
export function refreshCooldowns(ctx: SimContext): void {
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u) continue;
    for (const key of Object.keys(u.cooldowns).sort()) {
      const cd = u.cooldowns[key];
      if (cd) syncCooldown(cd, ctx.now);
    }
  }
}

function cooldownLength(ctx: SimContext, u: Unit, ab: AbilityDef): Ms {
  const spec = ab.cooldown;
  if (!spec) return 0;
  let dur = spec.durationMs;
  if (spec.hasteScaled) dur /= 1 + hasteOf(ctx, u.id);
  const rate = modSum(ctx, u, 'cooldownRatePct', ab.id);
  if (rate > -1) dur /= 1 + rate;
  return Math.max(0, Math.round(dur));
}

function consumeCharge(ctx: SimContext, u: Unit, ab: AbilityDef): void {
  if (!ab.cooldown) return;
  const maxCharges = Math.max(1, ab.cooldown.charges ?? 1);
  const dur = cooldownLength(ctx, u, ab);
  const cd = u.cooldowns[ab.id] ?? { charges: maxCharges, maxCharges, rechargeAt: null, rechargeDurationMs: dur };
  u.cooldowns[ab.id] = cd;
  syncCooldown(cd, ctx.now);
  cd.charges = Math.max(0, cd.charges - 1);
  if (cd.rechargeAt === null) {
    cd.rechargeAt = ctx.now + dur;
    cd.rechargeDurationMs = dur;
  }
  syncCooldown(cd, ctx.now); // a 0 ms cooldown comes straight back
}

/**
 * +lead: rotation rework (F2). What a press of `abilityId` casts right now: the override from
 * `unit.abilityOverrides` (if that ability exists), else the id itself. O(1), no conditions.
 */
export function resolveOverride(content: Pick<ContentRegistry, 'ability'>, u: DeepReadonly<Unit> | undefined, abilityId: AbilityId): AbilityId {
  const o = u?.abilityOverrides?.[abilityId];
  return o !== undefined && content.ability(o) ? o : abilityId;
}

// ---------------------------------------------------------------------------
// The system
// ---------------------------------------------------------------------------

export function createCastingSystem(): CastingSystem {
  const isOffGcdInstant = (ctx: SimContext, u: Unit, ab: AbilityDef): boolean =>
    (ab.gcdMs ?? BASE_GCD_MS) === 0 && castTimeOf(ctx, u, ab) === 0 && (ab.cast?.type ?? 'instant') !== 'channel';

  /** Hasted, aura-modified cast or channel duration (0 = instant). */
  function castTimeOf(ctx: SimContext, u: Unit, ab: AbilityDef): Ms {
    if (!ab.cast || ab.cast.type === 'instant') return 0; // no stat lookup for instants
    return castTimeFor(ctx.content, u, ab, hasteOf(ctx, u.id));
  }

  /** What currently blocks `ab` and for how long (0 = free now). */
  function busy(ctx: SimContext, u: Unit, ab: AbilityDef): { reason: CastFailReason; remaining: Ms } | null {
    const now = ctx.now;
    // TC Spell::prepare: SPELL_IN_PROGRESS while a hard cast is being prepared; off-GCD instants
    // weave only into channels (or with an explicit usableWhileCasting flag).
    if (u.cast && u.cast.kind === 'cast' && !ab.usableWhileCasting) {
      return { reason: 'casting', remaining: Math.max(0, u.cast.endsAt - now) };
    }
    if (u.cast && !isOffGcdInstant(ctx, u, ab)) {
      const left = Math.max(0, u.cast.endsAt - now);
      if (u.cast.kind === 'cast') return { reason: 'casting', remaining: left };
      // Channels: recasting the same ability is refused; anything else clips the channel
      // unless it is about to end, in which case it queues behind it.
      if (u.cast.abilityId === ab.id) return { reason: 'casting', remaining: left };
      if (left <= SPELL_QUEUE_WINDOW_MS && left > 0) return { reason: 'casting', remaining: left };
    }
    if ((ab.gcdMs ?? BASE_GCD_MS) > 0 && u.gcdUntil > now) return { reason: 'on_gcd', remaining: u.gcdUntil - now };
    const cd = sys.cooldownRemaining(ctx.world, u.id, ab.id);
    if (cd > 0) return { reason: 'on_cooldown', remaining: cd };
    return null;
  }

  /** Every check except the busy (cast/GCD/cooldown) check. Resolves the effective target. */
  function validate(ctx: SimContext, u: Unit, ab: AbilityDef, targetId: UnitId | null, point: Vec2 | null): Resolved {
    const w = ctx.world;
    const control = ctx.sys.combat.controlState(ctx, u.id);
    if (!control.canAct) return fail('cannot_act');
    if (!control.canCast) return fail('silenced');
    if (ab.requiresMobility && !control.canMove) return fail('rooted');
    const lock = u.lockouts[ab.school];
    if (lock !== undefined && lock > ctx.now) return fail('school_locked');

    const costMult = modMultiplier(ctx, u, 'resourceCostPct', ab.id);
    for (const c of ab.cost ?? []) {
      if ((u.resources[c.resource]?.cur ?? 0) < Math.round(c.amount * costMult)) return fail('no_resource');
    }
    // +mage-specs (S11): a spend-all ability needs at least its minimum in the pool.
    if (ab.spendAll && (u.resources[ab.spendAll.resource]?.cur ?? 0) < ab.spendAll.min) return fail('no_resource');

    // Target resolution (faction-relative so NPC abilities work the same way).
    let tgt: UnitId | null = null;
    let pt: Vec2 | null = null;
    switch (ab.targeting) {
      case 'self':
        tgt = u.id;
        break;
      case 'none':
      case 'cone':
        break;
      case 'ally': {
        const t = targetId ? w.units[targetId] : undefined;
        if (!t || t.faction !== u.faction) tgt = u.id;
        else if (!t.alive) return fail('invalid_target');
        else tgt = t.id;
        break;
      }
      case 'any': {
        // Friendly or hostile (Holy Shock). No/invalid selection falls back to self like 'ally'.
        const t = targetId ? w.units[targetId] : undefined;
        if (!t) tgt = u.id;
        else if (!t.alive) return fail('invalid_target');
        else tgt = t.id;
        break;
      }
      case 'enemy': {
        const t = targetId ? w.units[targetId] : undefined;
        if (!t) return fail('no_target');
        if (!t.alive || t.faction === u.faction) return fail('invalid_target');
        tgt = t.id;
        break;
      }
      case 'ground': {
        const t = targetId ? w.units[targetId] : undefined;
        pt = point ? { x: point.x, y: point.y } : t ? { x: t.pos.x, y: t.pos.y } : null;
        if (!pt) return fail('no_target');
        if (ab.range && ctx.sys.spatial.distance(u.pos, pt) > ab.range + u.combatReach) return fail('out_of_range');
        break;
      }
    }

    if (tgt !== null && tgt !== u.id) {
      const needsMelee = ab.melee === true || (ab.targeting === 'enemy' && !ab.range);
      if (needsMelee ? !ctx.sys.spatial.inMeleeRange(w, u.id, tgt) : ab.range ? !ctx.sys.spatial.inRange(w, u.id, tgt, ab.range) : false) {
        return fail('out_of_range');
      }
      if (ab.requiresFacing && !ctx.sys.spatial.isInFront(w, u.id, tgt, Math.PI)) return fail('not_facing');
    }

    if (ab.usableIf && !ctx.sys.combat.evalCondition(ctx, ab.usableIf, u.id, tgt)) return fail('condition_failed');

    // Players can't start a hard cast while moving; NPCs are stopped by startCast.
    if (u.kind === 'player' && castTimeOf(ctx, u, ab) > 0 && !ab.canCastWhileMoving && (u.move !== null || isAirborne(u, ctx.now))) return fail('moving'); // +jump: no hard casts mid-air
    // +glide: nothing but a dash (Dodge, leaps: they fold the glider) while gliding.
    if (u.jump?.air?.glide && !ab.effects?.some((e) => e.type === 'dash')) return fail('moving');

    return { ok: true, targetId: tgt, point: pt };
  }

  function lookup(ctx: SimContext, casterId: UnitId, abilityId: AbilityId): { u: Unit; ab: AbilityDef } | { ok: false; reason: CastFailReason } {
    const ab = ctx.content.ability(abilityId);
    if (!ab) return fail('unknown_ability');
    const u = ctx.world.units[casterId];
    if (!u || !u.alive) return fail('dead');
    return { u, ab };
  }

  /** Start an already-validated ability now (GCD, then instant/cast/channel). */
  function start(ctx: SimContext, u: Unit, ab: AbilityDef, targetId: UnitId | null, point: Vec2 | null): void {
    // A channel is clipped by a new on-GCD/cast ability (busy() allowed it); off-GCD instants weave.
    const offGcd = isOffGcdInstant(ctx, u, ab);
    if (u.cast && !offGcd && !(u.cast.kind === 'cast' && ab.usableWhileCasting)) sys.cancelCast(ctx, u.id, 'player');
    // An off-GCD instant weaves without dropping the queued GCD spell (throw → catch → next press).
    if (!offGcd || u.queued?.abilityId === ab.id) u.queued = null;
    const gcd = sys.gcdDuration(ctx, u.id, ab);
    if (gcd > 0) u.gcdUntil = ctx.now + gcd;

    const dur = castTimeOf(ctx, u, ab);
    const kind = ab.cast?.type ?? 'instant';
    if (dur === 0 && kind !== 'channel') {
      execute(ctx, u, ab, targetId, point, ctx.now, ctx.world.nextSeq, 0);
      startAttackFromAbility(ctx, u, ab); // +autoattack: a weapon ability starts auto-attack
      return;
    }
    startAttackFromAbility(ctx, u, ab); // +autoattack
    if (u.kind === 'npc' && !ab.canCastWhileMoving && u.move?.mode !== 'forced') u.move = null;
    const interruptible = ab.interruptible ?? u.kind === 'player';
    u.cast = {
      abilityId: ab.id,
      kind: kind === 'channel' ? 'channel' : 'cast',
      targetId,
      point,
      startedAt: ctx.now,
      endsAt: ctx.now + dur,
      durationMs: dur,
      interruptible,
      pushbacks: 0,
      startSeq: ctx.world.nextSeq,
    };
    ctx.emit({ type: 'begincast', sourceID: u.id, targetID: targetId ?? undefined, abilityGameID: ab.id, duration: dur, interruptible });
    if (kind === 'channel' && ab.cast?.type === 'channel') {
      const ticks = Math.max(1, ab.cast.ticks);
      u.cast.tickIntervalMs = Math.max(1, Math.round(dur / ticks));
      u.cast.ticksDone = 0;
      u.cast.nextTickAt = ctx.now + channelTickOffset(dur, ticks, 1);
      payAndAnnounce(ctx, u, ab, targetId, dur, true);
      // Channel start is the "cast" moment: cast procs fire once here, never on ticks.
      ctx.sys.combat.applyEffects(ctx, ab.channelStartEffects ?? [], { sourceId: u.id, abilityId: ab.id, targetId, point, isCast: true, castStartedAt: ctx.now, castStartSeq: u.cast.startSeq });
    }
  }

  /** Pay, announce and resolve a completed cast. `startedAt` feeds the castResolved same-cast guard (F3). */
  function execute(ctx: SimContext, u: Unit, ab: AbilityDef, targetId: UnitId | null, point: Vec2 | null, startedAt: Ms, startSeq: number | undefined, castMs: Ms): void {
    const spent = payAndAnnounce(ctx, u, ab, targetId, castMs, false);
    const ectx: EffectCtx = { sourceId: u.id, abilityId: ab.id, targetId, point, isCast: true, castStartedAt: startedAt };
    if (spent !== undefined) ectx.spent = spent; // +mage-specs (S11)
    if (startSeq !== undefined) ectx.castStartSeq = startSeq;
    ctx.sys.combat.applyEffects(ctx, ab.effects, ectx);
  }

  /**
   * +lead: rotation rework (F10). Run a channel's end effects: always when it completed, also on
   * cancel/interrupt with channelEndOn 'any'. Never on the caster's death.
   */
  function channelEnd(ctx: SimContext, u: Unit, c: CastState, completed: boolean): void {
    if (c.kind !== 'channel' || !u.alive) return;
    const ab = ctx.content.ability(c.abilityId);
    if (!ab?.channelEndEffects?.length) return;
    if (!completed && (ab.channelEndOn ?? 'complete') !== 'any') return;
    ctx.sys.combat.applyEffects(ctx, ab.channelEndEffects, { sourceId: u.id, abilityId: ab.id, targetId: c.targetId, point: c.point, depth: 0 });
  }

  /** Costs, gains, cooldown, 'cast' event (with castMs / channel, F12), flat threat. */
  function payAndAnnounce(ctx: SimContext, u: Unit, ab: AbilityDef, targetId: UnitId | null, castMs: Ms, channel: boolean): number | undefined {
    const costMult = modMultiplier(ctx, u, 'resourceCostPct', ab.id);
    for (const c of ab.cost ?? []) {
      const pool = u.resources[c.resource];
      if (pool) pool.cur = Math.max(0, pool.cur - Math.round(c.amount * costMult));
    }
    // +mage-specs (S11): spend-all empties the pool; the effects read what it took (EffectCtx.spent).
    let spent: number | undefined;
    if (ab.spendAll) {
      const pool = u.resources[ab.spendAll.resource];
      spent = pool ? Math.floor(pool.cur) : 0;
      if (pool) pool.cur = 0;
    }
    for (const g of ab.gain ?? []) {
      const pool = u.resources[g.resource];
      if (!pool) continue;
      const before = pool.cur;
      pool.cur = Math.min(pool.max, pool.cur + g.amount);
      ctx.emit({
        type: 'resourcechange',
        sourceID: u.id,
        targetID: u.id,
        abilityGameID: ab.id,
        resource: g.resource,
        resourceChange: g.amount,
        waste: g.amount - (pool.cur - before),
        resourceAmount: pool.cur,
        maxResourceAmount: pool.max,
      });
      // Energize threat (TC EffectEnergize): 0.5 per point gained, split across enemies.
      // Passive gainOn rules (rage from damage, TC RewardRage) stay threat-free.
      const gained = pool.cur - before;
      if (gained > 0) ctx.sys.combat.addHealingThreat(ctx, u.id, u.id, gained);
    }
    consumeCharge(ctx, u, ab);
    ctx.emit(
      channel
        ? { type: 'cast', sourceID: u.id, targetID: targetId ?? undefined, abilityGameID: ab.id, castMs, channel: true }
        : { type: 'cast', sourceID: u.id, targetID: targetId ?? undefined, abilityGameID: ab.id, castMs },
    );
    if (ab.flatThreat && targetId) {
      const t = ctx.world.units[targetId];
      if (t && t.kind === 'npc' && t.faction !== u.faction) ctx.sys.combat.addThreat(ctx, t.id, u.id, ab.flatThreat);
    }
    return spent;
  }

  function updateUnit(ctx: SimContext, u: Unit): void {
    const now = ctx.now;
    if (!u.alive) {
      if (u.cast) sys.cancelCast(ctx, u.id, 'death');
      u.queued = null;
      return;
    }
    const c = u.cast;
    if (c) {
      const ab = ctx.content.ability(c.abilityId);
      const control = ctx.sys.combat.controlState(ctx, u.id);
      const target = c.targetId ? ctx.world.units[c.targetId] : undefined;
      if (!ab) {
        u.cast = null;
      } else if (!control.canAct || !control.canCast) {
        sys.cancelCast(ctx, u.id, 'cc');
      } else if (u.move?.mode === 'forced' || (u.move !== null && !ab.canCastWhileMoving && u.kind === 'player')) {
        sys.cancelCast(ctx, u.id, 'moved');
      } else if (c.targetId && c.targetId !== u.id && (!target || !target.alive)) {
        sys.cancelCast(ctx, u.id, 'target_lost');
      } else if ((c.kind === 'channel' ? c.nextTickAt !== undefined && c.nextTickAt <= now : c.endsAt <= now) && !finalRangeOk(ctx, u, ab, c.targetId, c.point)) {
        sys.cancelCast(ctx, u.id, 'out_of_range');
      } else {
        if (u.kind === 'npc' && u.move !== null && !ab.canCastWhileMoving) u.move = null; // NPCs stand still to cast
        if (c.kind === 'channel') advanceChannel(ctx, u, ab);
        else if (c.endsAt <= now) {
          u.cast = null;
          execute(ctx, u, ab, c.targetId, c.point, c.startedAt, c.startSeq, Math.max(0, now - c.startedAt));
        }
      }
    }
    // Spell queue: fire as soon as the unit is free. During a channel the queued spell
    // clips it the moment busy() allows it (as a direct press would).
    const q = u.queued;
    if (q && (!u.cast || u.cast.kind === 'channel')) {
      const found = lookup(ctx, u.id, resolveOverride(ctx.content, u, q.abilityId)); // F2: resolve again at execution
      if (!('u' in found)) {
        u.queued = null;
        return;
      }
      if (now - q.queuedAt > QUEUE_EXPIRY_MS) {
        u.queued = null;
        return;
      }
      const blocked = busy(ctx, u, found.ab);
      if (blocked) return;
      u.queued = null;
      const v = validate(ctx, u, found.ab, q.targetId, q.point);
      if (v.ok) start(ctx, u, found.ab, v.targetId, v.point);
    }
  }

  /**
   * Range re-check when a cast completes / a channel ticks (TC Spell::cast -> CheckCast
   * non-strict): ab.range plus min(3, 10% of range) tolerance; spatial.inRange adds the
   * moving leeway. Melee and self/untargeted spells are not re-checked.
   */
  function finalRangeOk(ctx: SimContext, u: Unit, ab: AbilityDef, targetId: UnitId | null, point: Vec2 | null): boolean {
    const range = ab.range ?? 0;
    if (range <= 0 || ab.melee) return true;
    const tol = Math.min(3, range * 0.1);
    if (targetId && targetId !== u.id) return ctx.sys.spatial.inRange(ctx.world, u.id, targetId, range + tol);
    if (!targetId && point && ab.targeting === 'ground') return ctx.sys.spatial.distance(u.pos, point) <= range + u.combatReach + tol;
    return true;
  }

  function advanceChannel(ctx: SimContext, u: Unit, ab: AbilityDef): void {
    const c = u.cast;
    if (!c || ab.cast?.type !== 'channel') return;
    const ticks = Math.max(1, ab.cast.ticks);
    while ((c.ticksDone ?? 0) < ticks && c.nextTickAt !== undefined && c.nextTickAt <= ctx.now && c.nextTickAt <= c.endsAt) {
      c.ticksDone = (c.ticksDone ?? 0) + 1;
      c.nextTickAt = c.startedAt + channelTickOffset(c.durationMs, ticks, c.ticksDone + 1);
      ctx.sys.combat.applyEffects(ctx, ab.effects, { sourceId: u.id, abilityId: ab.id, targetId: c.targetId, point: c.point, depth: 0 });
      if (u.cast !== c) return; // an effect ended the channel (death, interrupt)
    }
    if (c.endsAt <= ctx.now) {
      u.cast = null;
      channelEnd(ctx, u, c, true);
    }
  }

  const sys: CastingSystem = {
    canCast(ctx, casterId, abilityId, targetId = null, point = null) {
      const found = lookup(ctx, casterId, resolveOverride(ctx.content, ctx.world.units[casterId], abilityId));
      if (!('u' in found)) return found;
      const { u, ab } = found;
      if (abilityLocked(ctx.content, u, abilityId, ab.id)) return fail('locked'); // +field
      const v = validate(ctx, u, ab, targetId, point);
      if (!v.ok) return v;
      const b = busy(ctx, u, ab);
      if (b) return fail(b.reason);
      return { ok: true, queued: false };
    },

    tryCast(ctx, casterId, abilityId, targetId = null, point = null) {
      // F2: a press of the base id casts its override; the queue keeps the pressed id.
      const found = lookup(ctx, casterId, resolveOverride(ctx.content, ctx.world.units[casterId], abilityId));
      if (!('u' in found)) return found;
      const { u, ab } = found;
      if (abilityLocked(ctx.content, u, abilityId, ab.id)) return fail('locked'); // +field
      const b = busy(ctx, u, ab);
      if (b && b.remaining <= SPELL_QUEUE_WINDOW_MS) {
        // Queue: target/range are re-validated when it fires.
        if (!ctx.sys.combat.controlState(ctx, u.id).canAct) return fail('cannot_act');
        u.queued = { abilityId, targetId: targetId ?? null, point: point ? { x: point.x, y: point.y } : null, queuedAt: ctx.now };
        return { ok: true, queued: true };
      }
      const v = validate(ctx, u, ab, targetId ?? null, point ?? null);
      if (!v.ok) return v;
      if (b) return fail(b.reason);
      start(ctx, u, ab, v.targetId, v.point);
      return { ok: true, queued: false };
    },

    update(ctx) {
      for (const id of ctx.world.unitOrder) {
        const u = ctx.world.units[id];
        if (u) updateUnit(ctx, u);
      }
    },

    executeAbility(ctx, casterId, abilityId, targetId, point) {
      const u = ctx.world.units[casterId];
      const ab = ctx.content.ability(abilityId);
      if (!u || !ab) return;
      execute(ctx, u, ab, targetId, point, ctx.now, ctx.world.nextSeq, 0);
    },

    interrupt(ctx, targetId, sourceId, byAbilityId, lockoutMs) {
      const t = ctx.world.units[targetId];
      const c = t?.cast;
      if (!t || !c || !c.interruptible || c.durationMs <= 0) return false;
      const ab = ctx.content.ability(c.abilityId);
      if (ab && lockoutMs > 0) t.lockouts[ab.school] = Math.max(t.lockouts[ab.school] ?? 0, ctx.now + lockoutMs);
      ctx.emit({ type: 'interrupt', sourceID: sourceId, targetID: targetId, abilityGameID: byAbilityId, extraAbilityGameID: c.abilityId });
      t.cast = null;
      t.queued = null;
      channelEnd(ctx, t, c, false);
      return true;
    },

    cancelCast(ctx, unitId, reason) {
      const u = ctx.world.units[unitId];
      if (!u) return;
      const c = u.cast;
      // A channel already at its end (clipped by a press in the tick it ends) completes first:
      // its last tick lands and its 'complete' end effects run (F10).
      if (c && c.kind === 'channel' && c.endsAt <= ctx.now && reason === 'player' && u.alive) {
        const ab = ctx.content.ability(c.abilityId);
        if (ab) advanceChannel(ctx, u, ab);
        if (u.cast === c) u.cast = null;
        return;
      }
      u.cast = null;
      if (reason === 'death' || reason === 'cc') u.queued = null;
      // A finished channel isn't "cancelled"; only report casts still in progress.
      if (c && c.endsAt > ctx.now) ctx.emit({ type: 'cast_cancelled', sourceID: u.id, abilityGameID: c.abilityId, reason });
      if (c && reason !== 'death') channelEnd(ctx, u, c, false);
    },

    pushback(ctx, unitId) {
      const u = ctx.world.units[unitId];
      const c = u?.cast;
      // TrinityCore: only player spells are delayed by damage.
      if (!u || !c || u.kind !== 'player' || c.pushbacks >= MAX_PUSHBACKS) return;
      if (c.kind === 'cast') {
        // TrinityCore: remaining time after the delay never exceeds the full cast time.
        const delay = Math.min(CAST_PUSHBACK_MS, c.durationMs - (c.endsAt - ctx.now));
        if (delay <= 0) return;
        c.endsAt += delay;
      } else {
        c.endsAt = Math.max(ctx.now, c.endsAt - Math.round(c.durationMs * CHANNEL_PUSHBACK_FRACTION));
      }
      c.pushbacks += 1;
    },

    cooldownRemaining(world: World, unitId, abilityId) {
      const cd = world.units[unitId]?.cooldowns[abilityId];
      if (!cd || cd.charges > 0 || cd.rechargeAt === null) return 0;
      return Math.max(0, cd.rechargeAt - world.time);
    },

    gcdDuration(ctx, unitId, ability) {
      // Only base GCDs inside [750, 1500] are hasted, so skip the stat lookup for the rest.
      const base = ability.gcdMs ?? BASE_GCD_MS;
      if (base <= 0 || base > BASE_GCD_MS || base < MIN_GCD_MS) return Math.max(0, base);
      return gcdFor(ability, hasteOf(ctx, unitId));
    },

    modifyCooldown(ctx, unitId, abilityId, change) {
      const u = ctx.world.units[unitId];
      if (!u) return;
      const cd = u.cooldowns[abilityId];
      if (!cd) {
        // Never used: every charge is up, so a granted charge is lost (F12).
        if (change.addCharge && !change.reset && ctx.content.ability(abilityId)?.cooldown) emitChargeOvercap(ctx, u.id, abilityId);
        return;
      }
      syncCooldown(cd, ctx.now);
      if (change.reset) {
        cd.charges = cd.maxCharges;
        cd.rechargeAt = null;
        return;
      }
      if (change.addCharge) {
        if (cd.charges < cd.maxCharges) {
          cd.charges += 1;
          if (cd.charges >= cd.maxCharges) cd.rechargeAt = null;
        } else emitChargeOvercap(ctx, u.id, abilityId);
      }
      if (change.deltaMs && cd.rechargeAt !== null) {
        cd.rechargeAt = Math.max(ctx.now, cd.rechargeAt + change.deltaMs);
        syncCooldown(cd, ctx.now);
      }
    },
  };
  return sys;
}

/** +lead: rotation rework (F12). A charge granted at max charges (sim-only event). */
function emitChargeOvercap(ctx: SimContext, unitId: UnitId, abilityId: AbilityId): void {
  ctx.emit({ type: 'overcap', sourceID: unitId, targetID: unitId, kind: 'charge', abilityGameID: abilityId, amount: 1 });
}

/** Offset of the i-th channel tick (1-based) from the channel start; the last lands on the end. */
function channelTickOffset(durationMs: Ms, ticks: number, i: number): Ms {
  return Math.round((durationMs * i) / ticks);
}
